import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClockCircleOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Segmented } from 'antd';
import dayjs from 'dayjs';
import ExtendEmploymentModal from '@/app/employees/_components/actions/ExtendEmploymentModal';
import EmployeePreviewModal from '@/app/employees/employee-profile/_components/EmployeePreviewModal';
import TerminateActionModal from '@/components/terminate-action-modal/TerminateActionModal';
import { ROLES_PERMISSIONS } from '@/constants';
import { cn } from '@/libs/utils';
import PrivateAction from '@/routes/PrivateAction';
import { DATE_FORMATS } from '@/utils/formUtils';
import { getUserFullName } from '@/utils/utils';

const PERIODS = [30, 60, 90];
const small = { buttonClassName: '!h-7 !rounded-[50px] !px-2.5 !text-xs' };
const fmt = (value) =>
  value ? dayjs(value).format(DATE_FORMATS.DISPLAY) : '—';

// employmentSettings.employmentType → label key in `forms`.
const TYPE_LABELS = {
  unlimited: 'unlimitedEmployment',
  indefinite: 'unlimitedEmployment',
  limited: 'limitedEmployment',
  ugovorODelu: 'contractWork',
  autorskiUgovor: 'authorContract',
  dopunskiRad: 'supplementaryWork',
  ppPoslovi: 'temporaryWork',
};

const Section = ({ title, extra, children }) => (
  <section className="min-w-0 rounded-xl border border-zinc-200 bg-white p-3">
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
      <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
        {title}
      </span>
      {extra}
    </div>
    {children}
  </section>
);

const Empty = ({ children }) => (
  <div className="py-4 text-center text-[12.5px] text-zinc-400">{children}</div>
);

/**
 * "Contracts" tile: the contract structure of the company, the contracts that
 * need a decision (with Extend / Terminate), the latest terminations and
 * extensions, and the register of signed contracts and annexes. A person opens
 * the quick employee preview.
 */
export default function CompanyContractsPanel({ companyId, stats }) {
  const { t } = useTranslation(['translations', 'forms']);
  const tc = (key, opts) =>
    t(`companyProfile.${key}`, { ns: 'translations', ...opts });
  const [days, setDays] = useState(60);
  const [previewId, setPreviewId] = useState(null);
  const [openType, setOpenType] = useState(null);

  const typeLabel = (type) =>
    TYPE_LABELS[type] ? t(TYPE_LABELS[type], { ns: 'forms' }) : tc('noType');

  // Contracts ended (profile still active) or ending within the period.
  const rows = useMemo(() => {
    const today = dayjs().startOf('day');
    return (stats?.contracts || [])
      .map((e) => {
        const end = dayjs(e.endDate).startOf('day');
        return { e, end, diff: end.diff(today, 'day') };
      })
      .filter(({ diff }) => diff <= days);
  }, [stats, days]);

  const byType = stats?.byEmploymentType || [];
  const maxType = Math.max(1, ...byType.map((g) => g.count));
  const changes = stats?.recentChanges || [];
  const register = stats?.register || { contracts: 0, annexes: 0, latest: [] };

  const person = (p) =>
    p ? (
      <button
        type="button"
        onClick={() => setPreviewId(p._id)}
        className="block max-w-full cursor-pointer truncate text-left text-[13px] font-bold text-zinc-900 hover:text-accent"
      >
        {getUserFullName(p.firstName, p.lastName)}
      </button>
    ) : (
      <span className="text-[13px] text-zinc-400">—</span>
    );

  return (
    <div className="grid gap-4 pt-3 xl:grid-cols-2">
      <Section title={tc('contractStructure')}>
        {byType.length ? (
          <ul className="grid gap-1">
            {byType.map((group) => {
              const open = openType === group.type;
              return (
                <li key={group.type}>
                  <button
                    type="button"
                    onClick={() => setOpenType(open ? null : group.type)}
                    className={cn(
                      'grid w-full cursor-pointer grid-cols-[150px_minmax(0,1fr)_40px] items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-zinc-50',
                      open && 'bg-[rgba(var(--accentColor),0.06)]'
                    )}
                  >
                    <span className="truncate text-[12.5px] font-semibold text-zinc-700">
                      {typeLabel(group.type)}
                    </span>
                    <span className="h-2.5 overflow-hidden rounded-full bg-zinc-100">
                      <span
                        className="block h-full rounded-full bg-accent"
                        style={{ width: `${(group.count / maxType) * 100}%` }}
                      />
                    </span>
                    <span className="text-right text-[13px] font-extrabold">
                      {group.count}
                    </span>
                  </button>
                  {open && (
                    <div className="flex max-h-48 flex-wrap gap-1.5 overflow-auto px-2 pb-2 pt-1">
                      {group.people.map((p) => (
                        <button
                          key={p._id}
                          type="button"
                          onClick={() => setPreviewId(p._id)}
                          className="cursor-pointer rounded-full border border-zinc-200 bg-white px-2.5 py-0.5 text-[12px] font-semibold text-zinc-700 hover:border-accent hover:text-accent"
                        >
                          {getUserFullName(p.firstName, p.lastName)}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty>—</Empty>
        )}
      </Section>

      <Section
        title={tc('needsDecision')}
        extra={
          <Segmented
            size="small"
            value={days}
            onChange={setDays}
            options={PERIODS.map((d) => ({
              value: d,
              label: tc('nextDays', { count: d }),
            }))}
          />
        }
      >
        {rows.length ? (
          <ul className="grid gap-1.5">
            {rows.map(({ e, end, diff }) => {
              const expired = diff < 0;
              return (
                <li
                  key={e._id}
                  className={cn(
                    'flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-2.5 py-2',
                    expired
                      ? 'border-red-200 bg-red-50'
                      : 'border-zinc-200 bg-white'
                  )}
                >
                  <span className="min-w-0 flex-1">
                    {person(e)}
                    <span className="block truncate text-[11.5px] text-zinc-500">
                      {e.position || '—'}
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="block text-[12.5px] font-bold text-zinc-800">
                      {end.format(DATE_FORMATS.DISPLAY)}
                    </span>
                    <span
                      className={cn(
                        'block text-[11px] font-semibold',
                        expired ? 'text-red-700' : 'text-amber-700'
                      )}
                    >
                      {expired
                        ? tc('expiredDaysAgo', { count: -diff })
                        : tc('expiresInDays', { count: diff })}
                    </span>
                  </span>
                  <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.CREATE}>
                    <span className="flex gap-1.5">
                      <ExtendEmploymentModal
                        metadata={small}
                        preset={{ employeeId: e._id, companyId }}
                      />
                      {/* An ended contract cannot be terminated (the server
                          does not offer it) — only extended. */}
                      {!expired && (
                        <TerminateActionModal
                          metadata={small}
                          employeeId={e._id}
                          companyId={companyId}
                          // The row leaves this list once terminated, so
                          // the notice is sent from the employee profile.
                          offerNotifyAfterSave={false}
                        />
                      )}
                    </span>
                  </PrivateAction>
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty>{tc('noExpiring')}</Empty>
        )}
      </Section>

      <Section title={tc('recentChanges')}>
        {changes.length ? (
          <ul className="grid gap-1">
            {changes.map((c) => {
              const isTermination = c.type === 'termination';
              return (
                <li
                  key={c._id}
                  className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-zinc-50"
                >
                  <span
                    className={cn(
                      'grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs',
                      isTermination
                        ? 'bg-red-50 text-red-600'
                        : 'bg-amber-50 text-amber-600'
                    )}
                  >
                    {isTermination ? <StopOutlined /> : <ClockCircleOutlined />}
                  </span>
                  <span className="min-w-0 flex-1">
                    {person(c.employee)}
                    <span className="block truncate text-[11.5px] text-zinc-500">
                      {isTermination ? tc('termination') : tc('extension')}
                      {c.endDate
                        ? ` · ${tc('until', { date: fmt(c.endDate) })}`
                        : ''}
                    </span>
                  </span>
                  <span className="text-[11.5px] text-zinc-400">
                    {fmt(c.date)}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty>{tc('noChangesYet')}</Empty>
        )}
      </Section>

      <Section
        title={tc('contractRegister')}
        extra={
          <span className="flex gap-1.5 text-[11.5px] font-semibold">
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-600">
              {tc('contractsCount', { count: register.contracts })}
            </span>
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-600">
              {tc('annexesCount', { count: register.annexes })}
            </span>
          </span>
        }
      >
        {register.latest.length ? (
          <ul className="grid gap-1">
            {register.latest.map((r) => (
              <li
                key={r._id}
                className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-zinc-50"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[rgba(var(--accentColor),0.1)] text-xs text-accent">
                  {r.kind === 'annex' ? (
                    <FileTextOutlined />
                  ) : (
                    <FileProtectOutlined />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  {person(r.employee)}
                  <span className="block truncate text-[11.5px] text-zinc-500">
                    {[
                      r.kind === 'annex'
                        ? tc('annex')
                        : typeLabel(r.employmentType),
                      r.number,
                      r.kind === 'annex'
                        ? r.subject
                        : r.endDate
                          ? `${fmt(r.startDate)} – ${fmt(r.endDate)}`
                          : fmt(r.startDate),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="text-[11.5px] text-zinc-400">
                  {fmt(r.signedDate)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>{tc('registerEmpty')}</Empty>
        )}
      </Section>

      <EmployeePreviewModal
        employeeId={previewId}
        onClose={() => setPreviewId(null)}
      />
    </div>
  );
}
